import { CompanyEntity, OrganisationWithBilling, isOrganisationWithBilling } from '@app.raytd.com/store'

export type FeatureKey =
  | 'advanced_reports'
  | 'export_pdf'
  | 'unlimited_projects'
  | 'test_suites_module'
  | 'element_groups_module'
  | 'inspections_module'
  | 'report_blocks_module'
  | 'asset_inspections_tab'
  | 'create_report_button'

/**
 * Determines if a feature is locked based on the organisation's billing status
 * @param feature - The feature key to check
 * @param organisation - The organisation entity (can be CompanyEntity or OrganisationWithBilling)
 * @returns true if the feature is locked (requires upgrade), false if unlocked
 */
export function isFeatureLocked(
  feature: FeatureKey,
  organisation: CompanyEntity | OrganisationWithBilling | null
): boolean {
  if (!organisation) return true

  // Check if organisation has billing info
  const hasBilling = isOrganisationWithBilling(organisation)
  if (!hasBilling) return true

  const billing = organisation.billing
  if (!billing) return true

  // Determine if organisation has Pro access:
  // - Subscribed (is_subscribed === true)
  // - On active trial (on_trial === true)
  // - Perpetual license (stripe_status === 'perpetual')
  const hasProAccess = 
    billing.is_subscribed === true || 
    billing.on_trial === true || 
    billing.stripe_status === 'perpetual'

  switch (feature) {
    case 'advanced_reports':
    case 'export_pdf':
    case 'unlimited_projects':
    case 'test_suites_module':
    case 'element_groups_module':
    case 'inspections_module':
    case 'report_blocks_module':
    case 'asset_inspections_tab':
    case 'create_report_button':
      return !hasProAccess // Locked if no Pro access
    default:
      return false
  }
}

/**
 * Get a human-readable feature name from a feature key
 */
export function getFeatureDisplayName(feature: FeatureKey): string {
  const names: Record<FeatureKey, string> = {
    advanced_reports: 'Advanced Reports',
    export_pdf: 'PDF Export',
    unlimited_projects: 'Unlimited Projects',
    test_suites_module: 'Test Suites',
    element_groups_module: 'Element Groups',
    inspections_module: 'Inspections',
    report_blocks_module: 'Report Blocks',
    asset_inspections_tab: 'Asset Inspections',
    create_report_button: 'Create Report',
  }
  return names[feature] || feature.replace(/_/g, ' ')
}

